import React, { useState, useEffect, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import pick from 'lodash/pick';
import set from 'lodash/set';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import InputField from 'src/presentation/components/forms/InputField';
import TablePhoneNumbers from './TablePhoneNumbers';
import FIELD_OPTIONS, {
  OptionType,
} from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SearchPhoneNumbers/fieldOptions';
import availableNumbersResource, {
  PhoneNumberCapability,
  PhoneNumberType,
} from 'src/actions/availableNumbers/availableNumbersResource';
import { Button, Checkbox } from 'src/presentation/components/agent_desktop_components';
import { Checkmark } from '@ad-scripts/presentation/components/common/checkbox';
import { FieldWrapper } from '@ad-scripts/presentation/components/common/input_field';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { mapToQueryObjectParams } from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SearchPhoneNumbers/helpers';
import { Row } from 'src/presentation/components/forms/Layout';
import {
  StepWizardActionContainer,
  StepWizardField,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { STATUS_FAILED, useResourceValues } from 'src/presentation/hooks/useResource';
import { toastError } from 'src/actions/toast';
import { useResetFormState } from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';

type PropsType = {
  entityType?: PhoneNumberCapability;
  isActive?: boolean;
  nextStep?;
  organizationSupportLink: string;
  previousStep?;
  saveStep: Function;
  stageConfig?;
};

const FilterPlaceHolders = {
  AREA_CODE: 'Search by 3-digit area code (e.g. 415)',
  CONTAINS: '439-4523 or HEY-GLAD',
  LOCALITY: 'Search by city name (e.g. “San Francisco”)',
};

export default function NewSearchPhoneNumbers({
  entityType,
  isActive,
  nextStep,
  organizationSupportLink,
  previousStep,
  saveStep,
  stageConfig,
}: PropsType) {
  const dispatch = useDispatch();

  const item = {
    countryCode: FIELD_OPTIONS.countryCodeOptions[0].value,
    numberType: FIELD_OPTIONS.numberTypeOptions[0].value,
    capabilities: FIELD_OPTIONS.capabilities.map(capability => {
      const isMatch = entityType?.toLowerCase() === capability.label.toLowerCase();
      return isMatch ? { ...capability, checked: true, disabled: true } : capability;
    }),
    filterType: FIELD_OPTIONS.filterTypeOptions[0].value,
    searchTerm: '',
  };

  const [searchFields, setSearchFields] = useState(item);

  const { loadingState } = useResourceValues(availableNumbersResource);

  useEffect(() => {
    if (loadingState === STATUS_FAILED) {
      dispatch(toastError("Couldn't load data. Reload the page."));
    }
  }, [dispatch, loadingState]);

  const formContext = useForm({
    onSave: ({ workingState }) => {
      dispatch(availableNumbersResource.effects.fetchAll(mapToQueryObjectParams(workingState)));
      setSearchFields(cloneDeep(formContext.workingState));
    },
    resetErrors: () => ({}),
    item,
  });

  const { workingState, updateState, onSave } = formContext;

  useEffect(() => {
    if (!isActive) {
      return;
    }
    dispatch(availableNumbersResource.effects.fetchAll(mapToQueryObjectParams(formContext.workingState)));
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterPlaceholder = FilterPlaceHolders[workingState.filterType as string];
  const countryCodeLetters = get(workingState, 'countryCode');
  const countryCode = useMemo(
    () =>
      get(
        find(FIELD_OPTIONS.countryCodeOptions, country => country.value === countryCodeLetters),
        'code',
        ''
      ),
    [countryCodeLetters]
  );
  const filterType = get(workingState, 'filterType');
  const searchInputPrefix = filterType === 'CONTAINS' ? countryCode : '';

  const handleCheckboxChange = (capability: string) => () => {
    const capabilities = cloneDeep(workingState.capabilities);
    const matchedItem = capabilities.find(item => item.label === capability) as OptionType;
    matchedItem.checked = !matchedItem.checked;
    updateState({ capabilities }, { replaceArrayValues: true });
  };

  const handleSelectPhone = selectedPhone => {
    saveStep({ selectedPhone });
    nextStep();
  };

  const handleChangeFilterType = filterType => {
    updateState({ searchTerm: '', filterType });
  };

  useResetFormState(item, updateState, stageConfig, entityType);

  const numberType = get(workingState, 'numberType');

  const filterTypeOptions = useMemo(() => {
    const options = FIELD_OPTIONS.filterTypeOptions;

    if (numberType === PhoneNumberType.TOLL_FREE) {
      updateState(set({}, 'filterType', 'AREA_CODE'));
      return filter(options, option => option.value !== 'LOCALITY');
    }
    return options;
  }, [numberType, updateState]);

  // Remove any extra attributes that may make dropdown proptypes unhappy
  const countryCodeOptions = FIELD_OPTIONS.countryCodeOptions.map(entry => pick(entry, ['label', 'value']));

  return (
    <Form context={formContext}>
      <StepWizardField>
        <H3 data-aid="wizard-title">Search phone numbers</H3>
        <StyledStepDescription>
          Looking to purchase an international number?{' '}
          <ExternalLink data-aid="submit-help" label="Submit a request" to={organizationSupportLink} />
        </StyledStepDescription>
        <StyledRow>
          <StyledCounterDropdown
            data-aid="country-code-dropdown"
            attr="countryCode"
            options={countryCodeOptions}
            label="Country"
          />
          <StyledNumberTypeDropdown
            data-aid="number-type-dropdown"
            attr="numberType"
            options={FIELD_OPTIONS.numberTypeOptions}
            label="Type"
          />
          <CapabilitiesContainer
            label="Capabilities"
            control={
              <>
                {formContext.workingState.capabilities.map(capability => (
                  <StyledCheckbox
                    className="capability"
                    onChange={handleCheckboxChange(capability.label)}
                    key={capability.label}
                    value={capability.label}
                    data-aid={capability.label}
                    {...capability}
                  />
                ))}
              </>
            }
          />
          <Button onClick={onSave}>Search</Button>
        </StyledRow>
        <StyledRow>
          <FieldContainer
            label="Filter numbers by"
            description="Narrow your options with digits or keywords (exact matches, not case sensitive)"
            control={
              <>
                <FilterTypeDropdown
                  data-aid="filter-type-dropdown"
                  attr="filterType"
                  options={filterTypeOptions}
                  onChange={handleChangeFilterType}
                />
                <StyledInputField
                  attr="searchTerm"
                  className="search-field"
                  placeholder={filterPlaceholder}
                  prefix={searchInputPrefix}
                  maxLength={filterType === 'AREA_CODE' ? 3 : undefined}
                  type={filterType === 'AREA_CODE' ? 'number' : 'text'}
                />
              </>
            }
          />
        </StyledRow>
      </StepWizardField>
      <TablePhoneNumbers searchFields={searchFields} handleSelectPhone={handleSelectPhone} />
      <StyledStepWizardActionContainer>
        <Button data-aid="editor-cancel" type={Button.Types.SECONDARY} onClick={previousStep}>
          Back
        </Button>
      </StyledStepWizardActionContainer>
    </Form>
  );
}

const StyledInputField = styled(InputField)`
  ${FieldWrapper} {
    &:before {
      content: '${p => p.prefix}';
      margin-top: 1px;
      padding-right: 4px;
    }
  }
`;

const FilterTypeDropdown = styled(Dropdown)`
  max-width: 160px;
`;

const StyledStepDescription = styled.p`
  line-height: 162%;
  margin-bottom: ${({ theme }) => theme.spacing.insetLarge};
  text-align: left;
  margin-bottom: ${({ theme }) => theme.spacing.insetMedium};
`;
const StyledRow = styled(Row)`
  align-items: end;
  width: inherit;
  margin-bottom: ${({ theme }) => theme.spacing.inlineLarge};

  ${FieldContainer} {
    margin: 0;
    margin-right: ${({ theme }) => theme.spacing.inlineMedium};
  }
`;

const StyledCounterDropdown = styled(Dropdown)`
  width: 320px;
`;

const StyledNumberTypeDropdown = styled(Dropdown)`
  width: 120px;
`;

const CapabilitiesContainer = styled(FieldContainer)`
  width: 185px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-right: ${({ theme }) => theme.spacing.inlineMedium};

  ${Checkmark} {
    border-radius: 2px;

    &[disabled] {
      border: 1px solid ${p => p.theme.colors.gray400};
      background: ${p => p.theme.colors.gray400};

      svg {
        fill: ${p => p.theme.colors.white};
      }
    }
  }

  div {
    margin-right: ${({ theme }) => theme.spacing.small};
  }
`;

const StyledStepWizardActionContainer = styled(StepWizardActionContainer)`
  padding-top: ${({ theme }) => theme.spacing.inlineLarge};
  border-top: 1px solid ${({ theme }) => theme.colors.gray300};
`;
